import React, { createContext, useContext, useReducer, ReactNode } from 'react'
import { ProductWithVariants } from 'types/product'
import { FilterOption } from 'types/filters'

export type BundlePiece = {
  product: ProductWithVariants
  selectedOptions: FilterOption[]
}

type State = {
  selectedPieces: Record<string, BundlePiece>
  lastViewedProducts: Record<string, string> // productHandle per type
}

type Action =
  | { type: 'SELECT_PIECE'; productType: string; piece: BundlePiece }
  | { type: 'SET_LAST_VIEWED'; productType: string; handle: string }
  | { type: 'RESET' }

type BundleContextValue = {
  state: State
  selectPiece: (productType: string, piece: BundlePiece) => void
  setLastViewed: (productType: string, handle: string) => void
  reset: () => void
}

const initialState: State = {
  selectedPieces: {},
  lastViewedProducts: {}
}

function reducer(state: State, action: Action): State {
  switch (action.type) {
    case 'SELECT_PIECE':
      return {
        ...state,
        selectedPieces: {
          ...state.selectedPieces,
          [action.productType]: action.piece
        }
      }

    case 'SET_LAST_VIEWED':
      return {
        ...state,
        lastViewedProducts: {
          ...state.lastViewedProducts,
          [action.productType]: action.handle
        }
      }

    case 'RESET':
      return initialState

    default: {
      throw new Error(`Unhandled action type`)
    }
  }
}

const BundleContext = createContext<BundleContextValue | undefined>(undefined)

export function BundleProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(reducer, initialState)

  const selectPiece = (productType: string, piece: BundlePiece) =>
    dispatch({ type: 'SELECT_PIECE', productType, piece })

  const setLastViewed = (productType: string, handle: string) =>
    dispatch({ type: 'SET_LAST_VIEWED', productType, handle })

  const reset = () => dispatch({ type: 'RESET' })

  const value: BundleContextValue = {
    state,
    selectPiece,
    setLastViewed,
    reset
  }

  return (
    <BundleContext.Provider value={value}>{children}</BundleContext.Provider>
  )
}

export function useBundleContext(): BundleContextValue {
  const context = useContext(BundleContext)

  if (!context) {
    throw new Error('useBundleContext must be used inside BundleProvider')
  }

  return context
}
